<template>
  <o-dialog
    ref="dialog"
    :title="$t('auth.password.changePassword.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('auth.password.changePassword.title'), type: 'confirm', callback: confirm, disabled: !valid },
    ]"
    :portal="portal"
  >
    <template #heading>
      <img
        v-if="account.employee.image"
        :src="account.employee.image"
        class="mx-auto h-12 w-12 rounded-full mt-3"
      >
    </template>

    <div class="pt-4 text-left">
      <o-form ref="form">
        <o-field-group v-slot="{ status }" :label="$t('auth.password.changePassword.oldPassword')" name="OldPassword" rules="required" class="block pb-3">
          <password-field v-model="oldPassword" :status="status" />
        </o-field-group>

        <o-field-group v-slot="{ status }" :label="$t('auth.password.new')" name="NewPassword" rules="required" class="block pb-3">
          <password-field v-model="newPassword" :status="status" />
        </o-field-group>

        <o-password-strength :password="newPassword" v-model:strength="strength" class="mb-6" />

        <o-field-group v-slot="{ status }" :label="$t('auth.password.confirm')" name="Confirm" rules="required|password:@NewPassword" class="block pb-3">
          <password-field v-model="confirmPassword" :status="status" />
        </o-field-group>

        <div class="mb-3 flex justify-end">
          <o-button flat size="sm" @click="handleGeneratePassword()">
            {{ $t('auth.password.suggest') }}
          </o-button>
        </div>

        <!--<o-field-group label="Revoke sessions" info="Log employee out of all active Ovatu sessions" col reverse class="">
          <toggle-field v-model="revokeSessions" />
        </o-field-group>-->
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import {
  OPasswordStrength,
} from '@/components/elements'

import {
  PasswordField,
  ToggleField,
} from '@/components/fields'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
}

withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $auth, $i18n, $notification } = useNuxtApp()

const dialog = ref<any>()
const form = ref<any>()
const revokeSessions = ref(false)
const oldPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const strength = ref(0)

const account = computed(() => $auth.info)

const info = computed(() => {
  const employee = account.value?.employee
  return `${employee?.firstName} ${employee?.lastName}`
})

const matches = computed(() => newPassword.value === confirmPassword.value)

const valid = computed(() => matches.value && oldPassword.value && strength.value > 2)

const open = () => {
  oldPassword.value = ''
  newPassword.value = ''
  confirmPassword.value = ''

  dialog.value.open()
}

const confirm = async () => {
  emit('update:loading', true)

  try {
    const success = await $api.auth.changePassword({
      oldPassword: oldPassword.value,
      newPassword: newPassword.value,
      confirmPassword: confirmPassword.value,
      revokeSessions: revokeSessions.value,
    })
    emit('update:loading', false)

    if (success) {
      $notification.toast({
        title: $i18n.t('auth.password.changeSuccess'),
        text: $i18n.t('auth.password.changePassword.successInfo'),
        variant: 'success',
      })

      emit('success')

      return true
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })

    emit('error')
  }
}

const handleGeneratePassword = () => {
  const suggested = Array(10)
    .fill('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz~!@-#$')
    .map(x => x[Math.floor(crypto.getRandomValues(new Uint32Array(1))[0] / (0xFFFFFFFF + 1) * x.length)])
    .join('')

  newPassword.value = suggested
  confirmPassword.value = suggested
}

defineExpose({ open })
</script>
